import { FONT_MONO } from '../../config/fonts';
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography, alpha } from "@mui/material";
import {
  AccountTree,
  CellTower,
  CenterFocusStrong,
  Close,
  Flag,
  MilitaryTechOutlined,
  Radar as RadarIcon,
  Search,
  Shield,
} from "@mui/icons-material";
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useTreeItemModel } from '@mui/x-tree-view/hooks';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useAuthContext } from "../../context/auth";
import socket from "../../utils/socket";
import { useUnitContext } from "../../context/unit";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { UNITS_SIDEBAR_WIDTH } from "./sidebarWidths";
import { OnlineStatusIcon } from "../OnlineStatusChip";
import { SidebarPanel, accentOf } from "./SidebarKit";

const MUTED = "rgba(255,255,255,0.6)";
const MONO = FONT_MONO;   // shared font (config/fonts.js)

// Unit types: 0 Command, 1 Regiment, 2 Company, 3 Outpost
const UNIT_TYPES = {
  0: { label: "Command", Icon: Shield },
  1: { label: "Regiment", Icon: AccountTree },
  2: { label: "Company", Icon: Flag },
  3: { label: "Outpost", Icon: CellTower },
};
const typeOf = (t) => UNIT_TYPES[t] || { label: "Unit", Icon: Shield };

// Tree row: type icon, name; outposts also get their number and the online / offline badge
// (same badge as the Users and Unit tables)
const UnitTreeItem = forwardRef(function UnitTreeItem(props, ref) {
  const item = useTreeItemModel(props.itemId);
  const { Icon } = typeOf(item?.type);
  const isOutpost = item?.type === 3;

  const label = (
    <Box
      component="span"
      title={isOutpost ? (item.online ? "Online" : "Offline") : undefined}
      sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, py: 0.15 }}
    >
      <Icon sx={{ fontSize: "1rem", opacity: 0.75, flexShrink: 0 }} />
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "0.82rem",
          fontWeight: isOutpost ? 500 : 700,
          opacity: isOutpost && !item.online ? 0.65 : 1,
        }}
      >
        {item?.name ?? props.label}
      </Box>
      {isOutpost && (
        <>
          <Box component="span" sx={{ fontFamily: MONO, fontSize: "0.68rem", color: MUTED, flexShrink: 0 }}>
            #{item.number}
          </Box>
          <OnlineStatusIcon online={item.online} size={16} />
        </>
      )}
    </Box>
  );

  return <TreeItem {...props} ref={ref} label={label} />;
});

// Helper to calculate distance between two lat/lng points (Haversine formula)
function getDistanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Calculate bounding box for children and return center and max distance
function getChildrenBoundingBox(children) {
  if (!children || children.length === 0) return null;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  children.forEach(child => {
    if (typeof child.latitude === "number" && typeof child.longitude === "number") {
      minLat = Math.min(minLat, child.latitude);
      maxLat = Math.max(maxLat, child.latitude);
      minLng = Math.min(minLng, child.longitude);
      maxLng = Math.max(maxLng, child.longitude);
    }
  });
  const center = [
    (minLat + maxLat) / 2,
    (minLng + maxLng) / 2
  ];
  // Calculate max distance from center to corners
  const maxDist = Math.max(
    getDistanceKm(center[0], center[1], minLat, minLng),
    getDistanceKm(center[0], center[1], minLat, maxLng),
    getDistanceKm(center[0], center[1], maxLat, minLng),
    getDistanceKm(center[0], center[1], maxLat, maxLng)
  );
  return { center, maxDist };
}

// Keep the units whose name or number matches, plus their parents (so the path stays visible)
const filterTree = (items, q) =>
  items.reduce((acc, it) => {
    const children = filterTree(it.children || [], q);
    const self = `${it.name} ${it.number ?? ""}`.toLowerCase().includes(q);
    if (self || children.length) acc.push({ ...it, children: self ? it.children : children });
    return acc;
  }, []);

const allIds = (items) => items.flatMap((it) => [it.id, ...allIds(it.children || [])]);

const UnitsSidebar = ({
  selectedUnit,
  setSelectedUnit,
  setCenterPosition,
  showRadarStation,
  setShowRadarStation,
  zoom,
  setZoom,
  mode = "live",        // 'live' (Radar) | 'replay' (Review)
}) => {
  const accent = accentOf(mode);
  const { auth } = useAuthContext();
  const axiosPrivate = useAxiosPrivate();

  const { selectableUnits = [], dispatch } = useUnitContext();

  const [syntheticRadius, setSyntheticRadius] = useState(3); // Default radius in kilometers
  const [query, setQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState(null);  // null = everything (until the user folds something)

  const findNodeById = (nodes, id) => {
    if (!nodes || !id) {
      return null;
    }

    const nodeArray = Array.isArray(nodes)
      ? nodes
      : [nodes];

    for (const node of nodeArray) {
      if (String(node._id) === String(id)) {
        return node;
      }

      if (Array.isArray(node.children) && node.children.length > 0) {
        const found = findNodeById(node.children, id);

        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  const transformToTreeViewItems = (units, level = 0) => {
    if (!units) return [];

    const arr = Array.isArray(units) ? units : [units];

    return arr.map((unit) => ({
      id: String(unit._id),
      label:
        unit.type === 3
          ? `${unit.name} (${unit.number})`
          : unit.name,
      name: unit.name,
      level,
      number: unit.number,
      type: unit.type,
      online: unit.isOnline === true,      // the API sends "isOnline" (outposts only)
      children: transformToTreeViewItems(
        unit.children || [],
        level + 1
      ),
    }));
  };

  const updateSyntheticRadius = async (node, newRadius) => {
    const radius = newRadius || 3;
    setSyntheticRadius(radius);

    // Dispatch update to selectableUnits
    dispatch({
      type: "UPDATE_UNIT_RADIUS",
      payload: {
        unitId: node._id,
        syntheticRadius: radius,
      },
    });

    fetchSelectableUnits(auth?._id);
  }

  const handleUnitClick = (node) => {
    setSelectedUnit(node);

    updateSyntheticRadius(node, syntheticRadius);

    // Set center as before
    if (node.type === 3 || node.type === 2) {
      setCenterPosition([node.latitude, node.longitude]);
      setZoom(8); // Example: closer zoom for outpost/company
    } else if (node.type === 1) {
      const bbox = getChildrenBoundingBox(node.children || []);
      if (bbox) {
        setCenterPosition(bbox.center);
        setZoom(7); // Example: mid zoom for regiment
      } else {
        setCenterPosition([-6.369028, 34.888822]);
        setZoom(6); // fallback
      }
    } else if (node.type === 0) {
      setCenterPosition([-6.369028, 34.888822]);
      setZoom(6); // default zoom for Command
    } else {
      setCenterPosition([-6.369028, 34.888822]);
      setZoom(6); // fallback
    }

    handleRadarStationToggle(false);  // Reset radar station visibility when selecting a new unit
  };

  const findFirstLeafNode = (nodes) => {
    if (!nodes) return null;
    if (!Array.isArray(nodes)) nodes = [nodes];

    for (const node of nodes) {
      if (!node.children || node.children.length === 0) {
        return node;
      }
      const childLeaf = findFirstLeafNode(node.children);
      if (childLeaf) return childLeaf;
    }

    return null;
  };

  const handleRadarStationToggle = (checked) => {
    setShowRadarStation(checked);

    if(selectedUnit?.type !== 3) {
      return;   // radar station view exists for outposts only
    }

    if(checked) {
      socket.emit('showRadar', selectedUnit._id);
    } else {
      socket.emit('hideRadar', selectedUnit._id);
    }
  }

  const fetchSelectableUnits = async (userId) => {
    const response = await axiosPrivate.get( "/api/units/selectable", { params: { userId }, });

    const data = Array.isArray(response.data) ? response.data : [response.data];

    dispatch({ type: "SET_SELECTABLE_UNITS", payload: data, });
  };

  useEffect(() => {
    fetchSelectableUnits(auth?._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?._id, dispatch]);

  // An outpost goes online / offline with its user: the server updates the unit, then sends
  // "presenceUpdate". Reload the tree then (several changes close together = one reload).
  const presenceTimer = useRef(null);
  useEffect(() => {
    if (!auth?._id) return;

    const onPresence = () => {
      clearTimeout(presenceTimer.current);
      presenceTimer.current = setTimeout(() => {
        fetchSelectableUnits(auth._id).catch((err) => console.error("Unable to refresh units", err));
      }, 1000);
    };

    socket.on("presenceUpdate", onPresence);
    return () => {
      socket.off("presenceUpdate", onPresence);
      clearTimeout(presenceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?._id]);

  useEffect(() => {
    if (!selectedUnit && selectableUnits && selectableUnits.length > 0) {
      const firstLeaf = findFirstLeafNode(selectableUnits);
      if (firstLeaf) {
        setSelectedUnit(firstLeaf);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableUnits, selectedUnit, setSelectedUnit]);

  useEffect(() => {
    if ( selectedUnit &&
      (selectedUnit.syntheticRadius === null ||
        selectedUnit.syntheticRadius === 0 ||
        typeof selectedUnit.syntheticRadius === "undefined")
    ) {
      setSyntheticRadius(3); // fallback default
    } else if (selectedUnit?.syntheticRadius) {
      setSyntheticRadius(selectedUnit.syntheticRadius);
    }
  }, [selectedUnit]);

  // ---------- Tree data ----------
  const treeItems = useMemo(
    () => transformToTreeViewItems(selectableUnits),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectableUnits]
  );

  const q = query.trim().toLowerCase();
  const visibleItems = useMemo(() => (q ? filterTree(treeItems, q) : treeItems), [treeItems, q]);

  // While searching everything is open; otherwise the user's own folding is kept
  const expanded = q ? allIds(visibleItems) : (expandedItems ?? allIds(treeItems));

  const outpostStats = useMemo(() => {
    let total = 0;
    let online = 0;
    const walk = (items) => items.forEach((it) => {
      if (it.type === 3) { total++; if (it.online) online++; }
      walk(it.children || []);
    });
    walk(treeItems);
    return { total, online };
  }, [treeItems]);

  const selected = selectedUnit?._id ? findNodeById(selectableUnits, String(selectedUnit._id)) || selectedUnit : null;
  const selectedType = typeOf(selected?.type);
  const isOutpost = selected?.type === 3;

  return (
    <SidebarPanel
      accent={accent}
      width={UNITS_SIDEBAR_WIDTH}
      sx={{ display: "flex", flexDirection: "column", overflowY: "hidden" }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <MilitaryTechOutlined sx={{ color: accent, fontSize: "1.2rem" }} />
        <Box flex={1} minWidth={0}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.14em" }}>UNITS</Typography>
          {outpostStats.total > 0 && (
            <Typography sx={{ fontSize: "0.68rem", color: MUTED }}>
              <Box component="span" sx={{ color: accent, fontWeight: 700, fontFamily: MONO }}>{outpostStats.online}</Box>
              {" / "}{outpostStats.total} outposts online
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh" arrow>
          <IconButton size="small" onClick={() => fetchSelectableUnits(auth?._id)} sx={{ color: MUTED, "&:hover": { color: "#fff" } }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Selected unit */}
      {selected && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: "10px",
            border: `1px solid ${alpha(accent, 0.35)}`,
            backgroundColor: alpha(accent, 0.08),
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                color: accent,
                backgroundColor: alpha(accent, 0.12),
                border: `1px solid ${alpha(accent, 0.4)}`,
              }}
            >
              <selectedType.Icon sx={{ fontSize: "1.05rem" }} />
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography noWrap sx={{ fontSize: "0.9rem", fontWeight: 700 }}>{selected.name}</Typography>
              <Typography noWrap sx={{ fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: alpha(accent, 0.9) }}>
                {selectedType.label}{isOutpost && selected.number != null ? ` · No. ${selected.number}` : ""}
              </Typography>
            </Box>
            {isOutpost && <OnlineStatusIcon online={selected.isOnline === true} size={18} />}
          </Box>

          <Box display="flex" gap={0.75} mt={1}>
            <Button
              size="small"
              fullWidth
              startIcon={<CenterFocusStrong />}
              onClick={() => { if (selected?._id) handleUnitClick(findNodeById(selectableUnits, String(selected._id)) || selected); }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "#fff",
                borderRadius: "8px",
                backgroundColor: "rgba(255,255,255,0.06)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
              }}
            >
              Center map
            </Button>

            {isOutpost && (
              <Tooltip title={showRadarStation ? "Hide the radar station view" : "Show this outpost's radar scan"} arrow>
                <Button
                  size="small"
                  fullWidth
                  aria-pressed={showRadarStation}
                  startIcon={<RadarIcon />}
                  onClick={() => handleRadarStationToggle(!showRadarStation)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderRadius: "8px",
                    color: showRadarStation ? "#0b0f12" : accent,
                    backgroundColor: showRadarStation ? accent : "transparent",
                    border: `1px solid ${alpha(accent, 0.6)}`,
                    "&:hover": { backgroundColor: showRadarStation ? accent : alpha(accent, 0.14) },
                  }}
                >
                  Radar view {showRadarStation ? "on" : "off"}
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {/* Search */}
      <TextField
        size="small"
        fullWidth
        placeholder="Search units or outpost no."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{
          mb: 1,
          "& .MuiOutlinedInput-root": {
            color: "#fff",
            fontSize: "0.8rem",
            borderRadius: "8px",
            backgroundColor: "rgba(255,255,255,0.05)",
            "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
            "&.Mui-focused fieldset": { borderColor: accent },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: "1rem", color: MUTED }} />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setQuery("")} sx={{ color: MUTED, p: 0.25 }}>
                <Close sx={{ fontSize: "0.95rem" }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Tree */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          mx: -0.5,
          px: 0.5,
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.2) transparent",
        }}
      >
        {visibleItems.length === 0 ? (
          <Typography sx={{ fontSize: "0.78rem", color: MUTED, textAlign: "center", mt: 3 }}>
            {q ? "No unit matches your search." : "No units available."}
          </Typography>
        ) : (
          <RichTreeView
            items={visibleItems}
            expandedItems={expanded}
            onExpandedItemsChange={(event, ids) => { if (!q) setExpandedItems(ids); }}
            selectedItems={selectedUnit?._id ? [String(selectedUnit._id)] : []}
            onSelectedItemsChange={(event, ids) => {
              const selectedId = Array.isArray(ids)
                ? ids[0]
                : ids;

              if (!selectedId) {
                return;
              }

              const node = findNodeById(
                selectableUnits,
                String(selectedId)
              );

              if (node) {
                handleUnitClick(node);
              }
            }}
            // Clicking the unit that is already selected doesn't change the selection, so the
            // handler above never runs: move the map to it here instead.
            onItemClick={(event, itemId) => {
              if (String(itemId) !== String(selectedUnit?._id)) return;

              const node = findNodeById(selectableUnits, String(itemId));
              if (node) {
                handleUnitClick(node);
              }
            }}
            slots={{
              item: UnitTreeItem,
              expandIcon: ChevronRightIcon,
              collapseIcon: ExpandMoreIcon,
            }}
            slotProps={{
              treeItem: {
                sx: {
                  color: "rgba(255,255,255,0.9)",
                  "& .MuiTreeItem-content": {
                    position: "relative",
                    borderRadius: "8px",
                    py: 0.25,
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                  },
                  "& .MuiTreeItem-iconContainer": { color: MUTED },
                  // Selected: accent tint + bar on the left (class / attribute differ across tree-view versions)
                  "&.Mui-selected > .MuiTreeItem-content, & .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content[data-selected]": {
                    backgroundColor: alpha(accent, 0.16),
                    color: "#fff",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 3,
                      borderRadius: 2,
                      backgroundColor: accent,
                    },
                  },
                  "& .MuiTreeItem-content.Mui-selected:hover, & .MuiTreeItem-content[data-selected]:hover": {
                    backgroundColor: alpha(accent, 0.22),
                  },
                  "& .MuiTreeItem-groupTransition": {
                    ml: 1.25,
                    pl: 0.75,
                    borderLeft: "1px dashed rgba(255,255,255,0.12)",
                  },
                },
              },
            }}
          />
        )}
      </Box>
    </SidebarPanel>
  );
};

export default UnitsSidebar;
